/**
 * Story #6490 (Bug #4838) 8.1 — "Revoked keys": every revoked key with its
 * deletion state, read from GET /cmk/keys so it survives a reload. The hierarchy
 * tree shows live keys only, so without this a revoked key — and whether its
 * search entries are really gone — vanished from the page.
 */
import PurgeStateNotice from './PurgeStateNotice'

export interface RevokedKeyRow {
  kms_key_arn: string
  key_type: string
  status: string
  scope_type?: string
  scope_id?: string
  key_alias?: string
  purge_state?: string | null
  purged_at?: string | null
  purge_alert_sent_at?: string | null
}

const REVOKED = new Set(['revoked', 'revoked_detected'])

export function revokedKeys<T extends RevokedKeyRow>(keys: T[]): T[] {
  return keys.filter((k) => REVOKED.has(k.status)
    && !k.key_type?.startsWith('migration#') && !k.key_type?.startsWith('rollback#'))
}

function scopeLabel(k: RevokedKeyRow): string {
  const scope = k.scope_type || 'org'
  return scope === 'org' ? 'Primary (workspace-wide) key' : `${scope[0].toUpperCase()}${scope.slice(1)} key — ${k.scope_id || 'unknown scope'}`
}

export default function RevokedKeysList({ keys }: { keys: RevokedKeyRow[] }) {
  const rows = revokedKeys(keys)
  if (rows.length === 0) return null
  return (
    <div data-part="RevokedKeysList" className="light-card shadow-sm p-5">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Revoked keys</h3>
      <p className="text-xs text-gray-500 mb-3">Revoking a key deletes the search entries and derived vectors it covered.</p>
      <ul className="space-y-3">
        {rows.map((k) => (
          <li key={k.key_type} data-testid="revoked-key" className="border-t border-gray-100 pt-3 first:border-0 first:pt-0">
            <p className="text-sm font-medium text-gray-900">{scopeLabel(k)}{k.key_alias ? ` (${k.key_alias})` : ''}</p>
            <div className="mt-1">
              <PurgeStateNotice state={k.purge_state} alertSentAt={k.purge_alert_sent_at} testId={`purge-state-${k.key_type}`} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
